import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { from, Subscription } from 'rxjs';
import { FormCompanyService } from 'src/app/layouts/main-v2/services/forms/form-company.service';
import { ICompany } from 'src/app/models/company.model';
import { IError } from 'src/app/models/error.model';
import { IModule } from 'src/app/models/module.model';
import { ITemplate } from 'src/app/models/template.model';
import { setWarningText } from 'src/app/reducers/warning-popup.reducer';
import { CompanyService } from 'src/app/services/company.service';
import { ModulesService } from 'src/app/services/modules.service';
import { AuthService } from 'src/app/services/auth.service';
import { TemplatesService } from 'src/app/services/templates.service';

@Component({
  selector: 'app-company-settings-v2',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.sass']
})
export class SettingsComponent implements OnInit, OnDestroy {
  loading = false;
  templatesLoading = true;
  isRoot = false;
  isEditMode = false;
  
  // Modal de anamnese custom
  showAnamneseQuestionsModal = false;
  
  // Modal de automações
  showAutomationModal = false;
  
  // CNPJ da empresa (para os modais)
  companyCnpj = '';

  // Form for config fields
  formSettings!: FormGroup;

  // Módulos disponíveis (carregados da API)
  modules: IModule[] = [];
  // Módulos selecionados
  selectedModules: IModule[] = [];

  // Templates da API
  allTemplates: ITemplate[] = [];
  activeTemplates: ITemplate[] = [];
  availableTemplates: ITemplate[] = [];
  
  // Listas filtradas para exibição
  activeTemplatesFiltered: ITemplate[] = [];
  availableTemplatesFiltered: ITemplate[] = [];
  
  // Termos de busca
  activeTemplatesSearch = '';
  availableTemplatesSearch = '';

  // Autentique token
  autentiqueToken = '';
  autentiqueTokenSaving = false;
  autentiqueTokenMessage = '';
  autentiqueTokenSuccess = false;
  hasAutentiqueToken = false;

  private saveSubscription?: Subscription;

  constructor(
    private readonly fb: FormBuilder,
    private readonly companyFormService: FormCompanyService,
    private readonly companyService: CompanyService,
    private readonly templatesService: TemplatesService,
    private readonly modulesService: ModulesService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly store: Store<{ warningPopup: IError }>,
    private readonly authService: AuthService
  ) {
    const permission = this.authService.getPermission();
    this.isRoot = permission === 'ROOT';
  }

  ngOnInit(): void {
    this.isEditMode = this.companyFormService.isEditMode();
    this.companyCnpj = (this.companyFormService.getCompanyId() || '').replace(/\D/g, '');
    this.buildForm();
    this.loadTemplates();
    this.loadModules();
    
    // Escuta o evento de save do header para salvar os dados no serviço
    // (o save na API é feito pelo company-form.component.ts)
    this.saveSubscription = this.companyFormService.onSaveRequest().subscribe(() => {
      this.saveFormDataToService();
    });
    
    // Se for modo de edição, popula o form quando os dados estiverem carregados
    if (this.isEditMode) {
      const savedInfo = this.companyFormService.getInfo();
      if (savedInfo) {
        this.populateForm();
        this.companyCnpj = (savedInfo.cnpj || this.companyCnpj).replace(/\D/g, '');
        this.hasAutentiqueToken = this.companyFormService.hasAutentiqueToken;
      } else {
        this.companyFormService.isDataLoaded().subscribe(loaded => {
          if (loaded) {
            this.populateForm();
            const info = this.companyFormService.getInfo();
            this.companyCnpj = (info?.cnpj || this.companyCnpj).replace(/\D/g, '');
            this.hasAutentiqueToken = this.companyFormService.hasAutentiqueToken;
          }
        });
      }
    }
  }

  private buildForm(): void {
    const savedInfo = this.companyFormService.getInfo();
    
    this.formSettings = this.fb.group({
      status: [savedInfo?.status ?? 'true'],
      contractedAnamneseType: [savedInfo?.contractedAnamneseType ?? 'NOT_CONTRACTED', Validators.required],
      funcionalidadesBeta: this.fb.group({
        planoTratamento: [savedInfo?.funcionalidadesBeta?.planoTratamento ?? false],
        calculadora: [savedInfo?.funcionalidadesBeta?.calculadora ?? false]
      }),
      pacienteModelo: [savedInfo?.pacienteModelo ?? 'desligado']
    });
  }

  private populateForm(): void {
    const savedInfo = this.companyFormService.getInfo();
    if (savedInfo) {
      this.formSettings.patchValue({
        status: savedInfo.status ?? 'true',
        contractedAnamneseType: savedInfo.contractedAnamneseType ?? 'NOT_CONTRACTED',
        funcionalidadesBeta: savedInfo.funcionalidadesBeta ?? {
          planoTratamento: false,
          calculadora: false
        },
        pacienteModelo: savedInfo.pacienteModelo ?? 'desligado'
      });
    }
  }

  private loadModules(): void {
    this.modulesService.listModules().subscribe({
      next: (modules) => {
        this.modules = modules;
        // Carrega módulos salvos do serviço
        const savedModules = this.companyFormService.getModules();
        if (savedModules && savedModules.length > 0) {
          this.selectedModules = [...savedModules];
        }
      }
    });
  }

  onModuleChange(event: Event, module: IModule): void {
    const input = event.target as HTMLInputElement;
    if (input.checked) {
      if (!this.selectedModules.some(m => m.id === module.id)) {
        this.selectedModules.push(module);
      }
    } else {
      const index = this.selectedModules.findIndex(m => m.id === module.id);
      if (index > -1) {
        this.selectedModules.splice(index, 1);
      }
    }
    
    // Atualiza o serviço imediatamente para garantir sincronização
    this.companyFormService.setModules(this.selectedModules);
  }

  isModuleSelected(module: IModule): boolean {
    return this.selectedModules.some(m => m.id === module.id);
  }

  private loadTemplates(): void {
    this.templatesLoading = true;
    
    from(this.templatesService.list([], this.modulesService.moduleId)).subscribe({
      next: (templates) => {
        this.allTemplates = (templates as any[]).map(t => ({
          id: t.id,
          name: t.name,
          order: t.order,
          tags: t.tags || []
        }));

        // Verifica se já tem templates salvos no serviço
        const savedTemplates = this.companyFormService.getTemplates();
        // Em modo edit, também verifica os templates originais da empresa
        const companyTemplates = this.companyFormService.getCompanyTemplates();
        
        if (savedTemplates && savedTemplates.length > 0) {
          this.activeTemplates = [...savedTemplates];
        } else if (companyTemplates && companyTemplates.length > 0) {
          this.activeTemplates = [...companyTemplates];
        } else {
          this.activeTemplates = [];
        }

        // Enriquece os templates ativos com as tags vindas da API (allTemplates),
        // pois os templates salvos/da empresa podem não conter tags
        this.activeTemplates = this.activeTemplates.map(active => {
          const fullTemplate = this.allTemplates.find(t => t.id === active.id);
          return {
            ...active,
            tags: active.tags?.length ? active.tags : (fullTemplate?.tags || [])
          };
        });

        // Templates disponíveis = todos os templates - templates ativos
        this.availableTemplates = this.allTemplates.filter(
          t => !this.activeTemplates.some(a => a.id === t.id)
        );

        // Ordena as listas alfabeticamente
        this.activeTemplates.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
        this.availableTemplates.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
        
        // Atualiza as listas filtradas
        this.updateTemplatesFilters();
        
        // Sincroniza os templates ativos com o serviço para garantir
        // que o company-form.component sempre tenha os dados corretos
        this.companyFormService.setTemplates(this.activeTemplates);

        this.templatesLoading = false;
      },
      error: () => {
        this.templatesLoading = false;
      }
    });
  }

  onTemplateChange(event: Event, template: ITemplate, isActive: boolean): void {
    const input = event.target as HTMLInputElement;
    
    if (isActive) {
      if (!input.checked) {
        const index = this.activeTemplates.findIndex(t => t.id === template.id);
        if (index > -1) {
          this.activeTemplates.splice(index, 1);
        }
        this.availableTemplates.push(template);
        this.availableTemplates.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
      }
    } else {
      if (input.checked) {
        const index = this.availableTemplates.findIndex(t => t.id === template.id);
        if (index > -1) {
          this.availableTemplates.splice(index, 1);
        }
        this.activeTemplates.push(template);
        this.activeTemplates.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
      }
    }

    this.updateTemplatesFilters();
    
    // Atualiza o serviço imediatamente para garantir que os templates
    // estejam sempre sincronizados quando o save for disparado
    this.companyFormService.setTemplates(this.activeTemplates);
  }

  isTemplateActive(template: ITemplate): boolean {
    return this.activeTemplates.some(t => t.id === template.id);
  }

  onActiveTemplatesSearchChange(): void {
    this.updateTemplatesFilters();
  }

  onAvailableTemplatesSearchChange(): void {
    this.updateTemplatesFilters();
  }

  private updateTemplatesFilters(): void {
    const activeSearch = this.activeTemplatesSearch.toLowerCase().trim();
    const availableSearch = this.availableTemplatesSearch.toLowerCase().trim();

    this.activeTemplatesFiltered = activeSearch
      ? this.activeTemplates.filter(t => t.name.toLowerCase().includes(activeSearch))
      : [...this.activeTemplates];

    this.availableTemplatesFiltered = availableSearch
      ? this.availableTemplates.filter(t => t.name.toLowerCase().includes(availableSearch))
      : [...this.availableTemplates];
  }

  goBack(): void {
    // Salva antes de voltar (sem validação, apenas preserva estado)
    this.persistFormDataToService();
    this.router.navigate(['../contact'], { relativeTo: this.route });
  }

  saveAndFinish(): void {
    if (this.formSettings.invalid) {
      this.store.dispatch(setWarningText("Preencha e valide todos os campos obrigatórios."));
      this.formSettings.markAllAsTouched();
      return;
    }

    // Valida Paciente Modelo + template PRESTACAO_DE_SERVICO_MODELO
    if (!this.validatePacienteModeloTemplates()) {
      return;
    }

    // Salva os módulos e templates no service
    this.companyFormService.setModules(this.selectedModules);
    this.companyFormService.setTemplates(this.activeTemplates);
    
    // Salva também os dados do form de settings no service
    this.saveFormDataToService();

    // No modo de edição, o save na API é feito pelo company-form.component.ts
    // Aqui apenas salvamos os dados no serviço
    if (this.isEditMode) {
      return;
    }

    // Modo de criação: faz o request para criar a empresa
    const allData = this.companyFormService.getAll();
    const info = allData.info;
    const address = allData.address;
    const contact = allData.contact;
    const settings = this.formSettings.value;

    const cnpj = info?.cnpj?.replace(/[^\d]+/g, '') || '';
    const imageValue = info?.image;
    const isNewFile = imageValue instanceof File;

    this.loading = true;

    const createWithLogo = (logoValue: string) => {
      const companyRequest: ICompany = {
        cnpj,
        corporate_name: info?.corporateName?.trim() || '',
        logo: logoValue,
        logo_position: info?.imagePosition === 'watermark' ? 'WATERMARK' : 'HEADERS',
        is_blocked: settings.status === 'false',
        contracted_anamnese_type: settings.contractedAnamneseType || 'NOT_CONTRACTED',
        use_default_anamnese: false,
        is_model_patient_active: settings.pacienteModelo === 'ativo',
        legal_email: contact?.legalEmail?.trim().toLowerCase() || '',
        address: {
          street: address?.street?.trim() || '',
          number: address?.number?.trim() || '',
          complement: address?.complement?.trim() || 'Sem complemento',
          neighborhood: address?.neighborhood?.trim() || '',
          city: address?.city?.trim() || '',
          state: address?.state?.trim() || '',
          zip_code: address?.cep?.trim() || ''
        },
        initial_admin: {
          name: contact?.ownerName?.trim() || '',
          email: contact?.ownerEmail?.trim().toLowerCase() || '',
          is_blocked: false,
          password: contact?.password || ''
        },
        documents: this.activeTemplates,
        witness_one_name: info?.firstWitnessName?.trim() || '',
        witness_one_document: info?.firstWitnessDocument?.replace(/\D/g, '') || '',
        witness_two_name: info?.secondWitnessName?.trim() || '',
        witness_two_document: info?.secondWitnessDocument?.replace(/\D/g, '') || '',
        sign_delivery_type: 'DELIVERY_METHOD_SMS',
        modules: this.selectedModules,
        has_treatment_plans: settings.funcionalidadesBeta?.planoTratamento || false,
        has_calculator: settings.funcionalidadesBeta?.calculadora || false,
        group_id: info?.groupId || undefined
      };

      this.companyService.createCompany(companyRequest)
        .subscribe({
          next: (response: any) => {
            this.loading = false;
            this.companyFormService.clear();
            const newCompanyId = response?.cnpj;
            if (newCompanyId) {
              this.router.navigate(['/main/v2/company', newCompanyId, 'edit', 'info']);
            } else {
              this.router.navigate(['/main/v2/company']);
            }
          },
          error: (error: any) => {
            this.loading = false;
            const message = error?.error?.message || error?.message || 'Erro ao criar empresa.';
            const status = error?.status || error?.statusCode || 500;
            this.store.dispatch(setWarningText(message, status));
          }
        });
    };

    if (isNewFile) {
      const file = imageValue as File;
      this.companyService.generateLogoUploadUrl(cnpj, file.name, file.type)
        .then(response => this.companyService.uploadFileToS3(response.uploadUrl, file).then(() => response.objectKey))
        .then(objectKey => createWithLogo(objectKey))
        .catch(() => {
          this.loading = false;
          this.store.dispatch(setWarningText('Erro ao enviar logo. Tente novamente.'));
        });
    } else {
      const logoValue = typeof imageValue === 'string' ? imageValue : '';
      createWithLogo(logoValue);
    }
  }

  // === MODAL DE ANAMNESE PERSONALIZADA ===
  openAnamneseQuestionsModal(): void {
    this.showAnamneseQuestionsModal = true;
  }

  closeAnamneseQuestionsModal(): void {
    this.showAnamneseQuestionsModal = false;
  }

  // === MODAL DE AUTOMAÇÕES ===
  openAutomationModal(): void {
    this.showAutomationModal = true;
  }

  closeAutomationModal(): void {
    this.showAutomationModal = false;
  }

  saveAutentiqueToken(): void {
    if (!this.autentiqueToken?.trim()) {
      this.autentiqueTokenMessage = 'Informe o token da API Autentique.';
      this.autentiqueTokenSuccess = false;
      return;
    }

    this.autentiqueTokenSaving = true;
    this.autentiqueTokenMessage = '';

    this.companyService.setClinicAutentiqueToken(this.companyCnpj, this.autentiqueToken.trim())
      .subscribe({
        next: () => {
          this.autentiqueTokenSaving = false;
          this.autentiqueTokenSuccess = true;
          this.autentiqueTokenMessage = 'Token salvo com sucesso. A assinatura retroativa dos documentos pendentes foi iniciada.';
          this.autentiqueToken = '';
          this.hasAutentiqueToken = true;
        },
        error: (error: any) => {
          this.autentiqueTokenSaving = false;
          this.autentiqueTokenSuccess = false;
          this.autentiqueTokenMessage = error?.error?.message || 'Erro ao salvar token. Verifique se o token é válido.';
        }
      });
  }

  removeAutentiqueToken(): void {
    this.autentiqueTokenSaving = true;
    this.autentiqueTokenMessage = '';

    this.companyService.setClinicAutentiqueToken(this.companyCnpj, '')
      .subscribe({
        next: () => {
          this.autentiqueTokenSaving = false;
          this.autentiqueTokenSuccess = true;
          this.autentiqueTokenMessage = 'Token removido com sucesso. A assinatura automática foi desativada.';
          this.hasAutentiqueToken = false;
        },
        error: (error: any) => {
          this.autentiqueTokenSaving = false;
          this.autentiqueTokenSuccess = false;
          this.autentiqueTokenMessage = error?.error?.message || 'Erro ao remover token.';
        }
      });
  }

  ngOnDestroy(): void {
    // Salva os dados automaticamente quando o componente for destruído (sem validação)
    this.persistFormDataToService();
    this.saveSubscription?.unsubscribe();
  }

  /**
   * Valida se, quando Paciente Modelo está ativo,
   * pelo menos um template ativo possui a tag PRESTACAO_DE_SERVICO_MODELO.
   */
  private validatePacienteModeloTemplates(): boolean {
    const settings = this.formSettings.value;
    if (settings.pacienteModelo !== 'ativo') {
      return true;
    }

    const hasModeloTemplate = this.activeTemplates.some(t =>
      t.tags?.some(tag => tag.description === 'PRESTACAO_DE_SERVICO_MODELO')
    );

    if (!hasModeloTemplate) {
      this.store.dispatch(
        setWarningText('Para ativar o Paciente Modelo, é necessário incluir um template de contrato de prestação de serviço de paciente modelo.')
      );
      return false;
    }

    return true;
  }

  private saveFormDataToService(): void {
    // Valida Paciente Modelo + template PRESTACAO_DE_SERVICO_MODELO
    if (!this.validatePacienteModeloTemplates()) {
      return;
    }

    this.persistFormDataToService();
  }

  /**
   * Persiste os dados do formulário no service SEM validação.
   * Usado pelo ngOnDestroy e goBack para preservar estado ao navegar entre steps.
   */
  private persistFormDataToService(): void {
    // Salva módulos e templates
    this.companyFormService.setModules(this.selectedModules);
    this.companyFormService.setTemplates(this.activeTemplates);
    
    // Preserva os dados de info existentes e adiciona os de settings
    const existingInfo = this.companyFormService.getInfo();
    const settings = this.formSettings.value;
    
    this.companyFormService.setInfo({
      ...existingInfo,
      status: settings.status,
      contractedAnamneseType: settings.contractedAnamneseType,
      funcionalidadesBeta: settings.funcionalidadesBeta,
      pacienteModelo: settings.pacienteModelo
    });
  }
}
